import React, { useEffect, useState } from "react";
import PageHeader from "../../../components/common/PageHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsisV,
  faPlus,
  faEye,
  faEdit,
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { convertToFollowup, getContactList } from "../api/contactService";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { useAuth } from "../../../context/AuthContext";
import ListPagination from "../../../components/common/ListPagination";
import {
  DEFAULT_PAGE_SIZE,
  parsePaginationResponse,
} from "../../../components/common/paginationUtils";
import "../../sales/inquiry/styles/inquiry.css";

interface Contact {
  id: number;
  name: string;
  phone: string;   // ✅ updated
  email?: string;
  company_name?: string;
  city?: string;
  state?: string;
}

const ContactList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const canConvertToFollowup =
    user?.role === "Admin" || user?.role === "Manager";

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showingRecords, setShowingRecords] = useState("");
  const [pageInfo, setPageInfo] = useState("");

  const [filters, setFilters] = useState({
    name: "",
    phone: "",
    email: "",
    source: "",
    vertical: "",
    assigned_to: "",
    status: "",
    from_date: "",
    to_date: "",
    company_name: "",
    city: "",
    state: "",
  });

  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  const [ordering, setOrdering] = useState("");
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isFetching, setIsFetching] = useState(false);

  const [openActionId, setOpenActionId] = useState<number | null>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  /* =====================
     SORTING
  ===================== */
  const getSortIcon = (field: string) => {
    if (ordering === field) return faSortUp;
    if (ordering === `-${field}`) return faSortDown;
    return faSort;
  };

  const toggleOrdering = (field: string) => {
    setPage(1); // ✅ add this
    setOrdering((prev) => {
      if (prev === field) return `-${field}`;
      if (prev === `-${field}`) return "";
      return field;
    });
  };

  /* =====================
     FETCH CONTACTS
  ===================== */
  const fetchContacts = async (pageNo: number, appliedFilters = filters) => {
    if (isInitialLoad) setIsInitialLoad(true);
    else setIsFetching(true);

    try {
      const res = await getContactList(pageNo, {
        page_size: pageSize,
        name: appliedFilters.name,
        phone: appliedFilters.phone,
        email: appliedFilters.email,
        source: appliedFilters.source,
        vertical: appliedFilters.vertical,
        assigned_to: appliedFilters.assigned_to,
        status: appliedFilters.status,
        from_date: appliedFilters.from_date,
        to_date: appliedFilters.to_date,
        company_name: appliedFilters.company_name,
        city: appliedFilters.city,
        state: appliedFilters.state,
        ordering,
      });

      const pagination = parsePaginationResponse(res.data, {
        fallbackPageSize: pageSize,
        fallbackPage: pageNo,
      });

      setContacts(pagination.results as Contact[]);
      setTotalCount(pagination.totalCount);
      setShowingRecords(pagination.showingRecords);
      setPageInfo(pagination.pageInfo);

    } finally {
      setIsFetching(false);
      setIsInitialLoad(false);
    }
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(1);
  };

  useEffect(() => {
    fetchContacts(page, debouncedFilters);
  }, [page, pageSize, ordering, debouncedFilters]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      setDebouncedFilters((prev) => {
        if (JSON.stringify(prev) === JSON.stringify(filters)) {
          return prev;
        }
        setPage(1);
        return filters;
      });
    }, 500);

    return () => clearTimeout(debounce);
  }, [filters]);

  useEffect(() => {
    const handleClickOutside = () => {
      setOpenActionId(null);
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const handleConvert = async (id: number) => {
    if (!canConvertToFollowup) {
      toast.error("Only Admin or Manager can convert contact to followup");
      return;
    }

    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Convert this contact to followup?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, convert",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#198754",
      cancelButtonColor: "#6c757d",
    });

    if (!result.isConfirmed) return;

    try {
      await convertToFollowup(id);

      await Swal.fire({
        icon: "success",
        title: "Converted!",
        text: "Contact converted successfully",
        timer: 1500,
        showConfirmButton: false,
      });

      // 🔥 refresh list
      fetchContacts(page, debouncedFilters);
    } catch (err: any) {
      console.error(err);

      const data = err?.response?.data;

      let messages: string[] = [];

      // ✅ CASE 1: { error: { field: "msg" } }
      if (data?.error && typeof data.error === "object") {
        Object.values(data.error).forEach((msg: any) => {
          if (typeof msg === "string") messages.push(msg);
        });
      }

      // ✅ CASE 2: { field: ["msg"] }
      else if (data && typeof data === "object") {
        Object.values(data).forEach((val: any) => {
          if (Array.isArray(val)) {
            messages.push(...val);
          }
        });
      }

      // ✅ SHOW ALL ERRORS
      if (messages.length) {
        toast.error(messages.join(", "));
        return;
      }

      // fallback
      toast.error("Failed to convert followup");
    }
  };
  return (
    <>
      <PageHeader
        title="Contact Directory"
        breadcrumbs={[
          { label: "Dashboard", path: "/dashboard" },
          { label: "Masters" },
          { label: "Contact Directory" },
        ]}
      />

      <div className="container mt-4">
        {/* ADD BUTTON */}
        <div className="d-flex justify-content-end mb-4">
          <button
            className="btn btn-primary"
            onClick={() => navigate("/masters/contact/create")}
          >
            <FontAwesomeIcon icon={faPlus} /> Add Contact
          </button>
        </div>

        <div className="card hr-card">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>Action</th>

                  <th
                    onClick={() => toggleOrdering("name")}
                    className="sortable"
                  >
                    Name <FontAwesomeIcon icon={getSortIcon("name")} />
                  </th>

                  <th>Mobile</th>
                  <th>Email</th>
                  <th>Company</th>

                  <th
                    onClick={() => toggleOrdering("city")}
                    className="sortable"
                  >
                    City <FontAwesomeIcon icon={getSortIcon("city")} />
                  </th>

                  <th
                    onClick={() => toggleOrdering("state")}
                    className="sortable"
                  >
                    State <FontAwesomeIcon icon={getSortIcon("state")} />
                  </th>
                </tr>

                {/* FILTER ROW */}
                <tr className="table-filter-row">
                  <th></th>

                  <th>
                    <input
                      className="form-control form-control-sm"
                      placeholder="Search name..."
                      value={filters.name}
                      onChange={(e) =>
                        setFilters((prev) => ({ ...prev, name: e.target.value }))
                      }
                    />
                  </th>

                  <th>
                    <input
                      className="form-control form-control-sm"
                      placeholder="Search mobile..."
                      value={filters.phone}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "" || /^\d+$/.test(val)) {
                          setFilters((prev) => ({ ...prev, phone: val }));
                        }
                      }}
                    />
                  </th>

                  <th>
                    <input
                      className="form-control form-control-sm"
                      placeholder="Search email..."
                      value={filters.email}
                      onChange={(e) =>
                        setFilters((prev) => ({ ...prev, email: e.target.value }))
                      }
                    />
                  </th>

                  <th>
                    <input
                      className="form-control form-control-sm"
                      placeholder="Search company..."
                      value={filters.company_name}
                      onChange={(e) =>
                        setFilters((prev) => ({ ...prev, company_name: e.target.value }))
                      }
                    />
                  </th>

                  <th>
                    <input
                      className="form-control form-control-sm"
                      placeholder="Search city..."
                      value={filters.city}
                      onChange={(e) =>
                        setFilters((prev) => ({ ...prev, city: e.target.value }))
                      }
                    />
                  </th>

                  <th>
                    <input
                      className="form-control form-control-sm"
                      placeholder="Search state..."
                      value={filters.state}
                      onChange={(e) =>
                        setFilters((prev) => ({ ...prev, state: e.target.value }))
                      }
                    />
                  </th>
                </tr>
              </thead>

              <tbody>
                {isInitialLoad ? (
                  <tr>
                    <td colSpan={7} className="text-center py-4">
                      Loading...
                    </td>
                  </tr>
                ) : contacts.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-4">
                      No contacts found
                    </td>
                  </tr>
                ) : (
                  contacts.map((c) => (
                    <tr key={c.id}>
                      <td className="position-relative">
                        <FontAwesomeIcon
                          icon={faEllipsisV}
                          className="cursor-pointer"
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.stopPropagation();

                            const rect = e.currentTarget.getBoundingClientRect();
                            const DROPDOWN_WIDTH = 140;
                            const PADDING = 8;

                            let left = rect.right - DROPDOWN_WIDTH;
                            if (left < PADDING) {
                              left = PADDING;
                            }

                            setDropdownPos({
                              top: rect.bottom + 6,
                              left,
                            });

                            setOpenActionId(openActionId === c.id ? null : c.id);
                          }}
                        />
                      </td>

                      <td>{c.name}</td>
                      <td>{c.phone}</td>
                      <td>{c.email || <span className="text-muted">—</span>}</td>
                      <td>{c.company_name || "—"}</td>
                      <td>{c.city || "—"}</td>
                      <td>{c.state || "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {openActionId !== null && (
            <div
              className="inquiry-action-dropdown fixed"
              style={{
                top: dropdownPos.top,
                left: dropdownPos.left,
                width: 140,
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="view"
                onClick={() => {
                  navigate(`/masters/contact/view/${openActionId}`);
                  setOpenActionId(null);
                }}
              >
                <FontAwesomeIcon icon={faEye} /> View
              </button>

              <button
                type="button"
                onClick={() => {
                  navigate(`/masters/contact/edit/${openActionId}`);
                  setOpenActionId(null);
                }}
              >
                <FontAwesomeIcon icon={faEdit} /> Edit
              </button>

              {canConvertToFollowup && (
                <button
                  type="button"
                  onClick={() => {
                    handleConvert(openActionId);
                    setOpenActionId(null);
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} /> Convert to Followup
                </button>
              )}
            </div>
          )}

          <ListPagination
            page={page}
            totalCount={totalCount}
            pageSize={pageSize}
            currentCount={contacts.length}
            showingRecords={showingRecords}
            pageInfo={pageInfo}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
            className="px-3"
          />
        </div>
      </div>
    </>
  );
};

export default ContactList;